import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy, Users, Shield, ArrowRight, Sparkles, CheckCircle, Zap, Smartphone, Building2, Gift, Crown } from 'lucide-react';
import { Button, DarkModeToggleCompact } from '../../components/ui';

export const WkPouleLanding = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "WK Bedrijfspoule 2026 Maken | ProPoule Voetbalpool Software"
        const metaDesc = document.querySelector('meta[name="description"]')
        if (metaDesc) {
            metaDesc.setAttribute("content", "Organiseer de ultieme WK Bedrijfspoule 2026 voor collega's of vrienden met ProPoule. Volledig geautomatiseerde voetbalpool in eigen huisstijl met logo.")
        }
    }, [])

    return (
        <div className="min-h-screen bg-[#050505] text-white selection:bg-orange-500 selection:text-white relative overflow-x-hidden font-sans">
            {/* Background Effects */}
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-gradient-to-br from-orange-500/20 to-transparent rounded-full blur-[120px] pointer-events-none"></div>
            <div className="absolute bottom-0 right-[-10%] w-[60%] h-[60%] bg-gradient-to-br from-purple-900/20 to-transparent rounded-full blur-[120px] pointer-events-none"></div>

            {/* 🧭 Nette Navigatie */}
            <nav className="fixed w-full z-50 top-0 transition-all duration-300 bg-black/60 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-3 group cursor-pointer" onClick={() => navigate('/wkpoule')}>
                        <Trophy className="text-orange-500 h-6 w-6 group-hover:scale-110 transition-transform" />
                        <span className="font-black text-xl tracking-tighter uppercase italic select-none flex gap-1">
                            <span className="text-white">WK</span>
                            <span className="text-orange-500">POULE</span>
                        </span>
                    </div>

                    {/* Central Brand Navigator Pill */}
                    <div className="hidden md:flex items-center gap-6 bg-white/5 border border-white/10 rounded-2xl px-6 py-2 backdrop-blur-lg">
                        <button onClick={() => navigate('/')} className="text-xs font-bold text-slate-300 hover:text-orange-400 transition-colors uppercase tracking-widest">⚽ ProPoule</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/wkpoule')} className="text-xs font-bold text-orange-400 uppercase tracking-widest">🏆 WK Poule</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/e-sports')} className="text-xs font-bold text-slate-300 hover:text-purple-400 transition-colors uppercase tracking-widest">🎮 ProPoule Esports</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/proracket')} className="text-xs font-bold text-slate-300 hover:text-green-400 transition-colors uppercase tracking-widest">🎾 ProPoule Racket</button>
                    </div>

                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={() => navigate('/login?redirectTo=/wkpoule/dashboard')} className="hidden md:flex text-gray-300 hover:text-white">
                            Inloggen
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={() => navigate('/login?redirectTo=/wkpoule/dashboard')}
                            className="bg-orange-500 hover:bg-orange-600 border-0 shadow-lg shadow-orange-500/20 font-bold"
                        >
                            Poule Starten
                        </Button>
                    </div>
                </div>
            </nav>

            {/* 🚀 HERO SECTION (Direct Answer Box) */}
            <section className="relative pt-32 md:pt-44 pb-20 px-6 flex items-center">
                <div className="max-w-7xl mx-auto w-full">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="text-left animate-slideUp">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-500 mb-6 text-xs font-black uppercase tracking-widest">
                                <Sparkles size={14} />
                                <span>Hét #1 Platform voor Bedrijfspoules</span>
                            </div>
                            
                            {/* SEO Optimized H1 */}
                            <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 leading-[1.05]">
                                Organiseer de ultieme <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-pink-500 drop-shadow-xl">
                                    WK Bedrijfspoule
                                </span>
                            </h1>
                            
                            <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-xl leading-relaxed">
                                Stop met saaie Excel-lijsten. Kies voor een professionele, volledig geautomatiseerde voetbalpool met live standen, jokers en <strong>je eigen bedrijfslogo</strong>.
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4 mb-12">
                                <Button 
                                    onClick={() => navigate('/login?redirectTo=/wkpoule/dashboard')}
                                    size="xl" 
                                    className="bg-orange-500 hover:bg-orange-400 text-white shadow-[0_0_30px_rgba(249,115,22,0.4)] rounded-xl px-8 py-4 text-lg font-black transition-all active:scale-95 flex items-center justify-center border-0"
                                >
                                    Start Nu Gratis <ArrowRight size={20} className="ml-2" />
                                </Button>
                                <div className="flex items-center justify-center sm:justify-start gap-2 text-sm text-gray-500">
                                    <Shield size={16} className="text-green-500" />
                                    Geen creditcard nodig
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-6 py-6 border-t border-white/10 max-w-lg">
                                <div className="flex items-center gap-2 font-bold text-sm text-gray-300"><CheckCircle size={16} className="text-orange-500" /> Live Standen</div>
                                <div className="flex items-center gap-2 font-bold text-sm text-gray-300"><CheckCircle size={16} className="text-orange-500" /> 100% Automatisch</div>
                                <div className="flex items-center gap-2 font-bold text-sm text-gray-300"><CheckCircle size={16} className="text-orange-500" /> Mobile First</div>
                            </div>
                        </div>

                        {/* Visual Representation */}
                        <div className="relative flex justify-center items-center group">
                            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 to-pink-500/20 rounded-full blur-3xl group-hover:opacity-60 transition-opacity"></div>
                            <div className="relative w-full max-w-md aspect-[9/16] bg-[#111] rounded-[3rem] border-[8px] border-gray-900 shadow-2xl overflow-hidden rotate-3 hover:rotate-0 transition-all duration-700">
                                <div className="absolute top-0 inset-x-0 h-6 bg-black rounded-b-3xl w-32 mx-auto z-20"></div>
                                <div className="h-full w-full bg-[#0f0f0f] flex flex-col p-6 pt-12">
                                    <div className="flex justify-between items-center mb-6 border-b border-white/5 pb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 bg-orange-500/20 rounded flex items-center justify-center text-orange-500 font-black text-xs">LOGO</div>
                                            <div className="text-sm font-bold">Kantoor Poule</div>
                                        </div>
                                        <Crown className="text-yellow-500" size={20} />
                                    </div>
                                    <div className="space-y-3 opacity-80">
                                        {[1, 2, 3].map((i) => (
                                            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 flex justify-between items-center">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-6 h-6 bg-gray-800 rounded-full border border-white/10"></div>
                                                    <div className="text-xs font-bold text-gray-400">Team A</div>
                                                </div>
                                                <div className="flex gap-2 font-black text-white"><span>2</span><span>-</span><span>1</span></div>
                                                <div className="flex items-center gap-3">
                                                    <div className="text-xs font-bold text-gray-400">Team B</div>
                                                    <div className="w-6 h-6 bg-gray-800 rounded-full border border-white/10"></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-auto bg-gradient-to-br from-orange-500 to-pink-500 p-4 rounded-xl text-center shadow-lg relative overflow-hidden">
                                        <Zap size={40} className="absolute -right-2 -top-2 opacity-20 text-white" />
                                        <div className="font-black text-sm text-white">10 PUNTEN BEHAALD!</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 💎 FEATURES SECTION (The Value Prop) */}
            <section className="py-24 bg-[#080808] border-y border-white/5">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-black mb-4">Alles voor de ultieme werksfeer</h2>
                        <p className="text-gray-500 max-w-2xl mx-auto">Waarom kiezen voor de ProPoule WK software? Simpel.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:border-orange-500/30 transition-all">
                            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center text-orange-500 mb-6"><Building2 size={24} /></div>
                            <h3 className="text-xl font-bold mb-3">Volledig in Huisstijl</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Geen vreemde merken. Jouw eigen bedrijfslogo prominent bovenaan de ranglijst en voorspellingspagina.</p>
                        </div>
                        <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:border-orange-500/30 transition-all">
                            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center text-orange-500 mb-6"><Zap size={24} /></div>
                            <h3 className="text-xl font-bold mb-3">Het Joker Systeem</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Zet 5 tactische jokers in om punten te verdubbelen. Voegt een extra laag strategie toe aan het kantoorspel.</p>
                        </div>
                        <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:border-orange-500/30 transition-all">
                            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center text-orange-500 mb-6"><Smartphone size={24} /></div>
                            <h3 className="text-xl font-bold mb-3">Geen App Download Nodig</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Werkt direct in de browser van elke smartphone en desktop. Eén linkje delen via de WhatsApp-knop is genoeg.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 💰 TARIEF CTA */}
            <section className="py-20 relative overflow-hidden">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-[3rem] p-10 md:p-16 relative text-center overflow-hidden shadow-2xl">
                        <div className="absolute top-0 right-0 p-10 opacity-10"><Trophy size={150} fill="currentColor" /></div>
                        
                        <h2 className="text-3xl md:text-5xl font-black text-white mb-6">Direct Live voor jouw team?</h2>
                        <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
                            Start direct een gratis poule voor je vrienden, of upgrade naar de <strong>Premium Bedrijfspoule</strong> voor de ultieme merkbeleving.
                        </p>

                        <div className="flex flex-col md:flex-row justify-center items-center gap-6">
                            <div className="bg-black/50 border border-orange-500/30 rounded-2xl p-6 w-full md:w-72 flex flex-col items-center">
                                <span className="text-xs font-black tracking-widest text-gray-500 uppercase mb-2">Eenmalig tarief</span>
                                <span className="text-4xl font-black text-white">€65,00</span>
                                <span className="text-xs text-gray-400 mt-1">Geen verborgen abonnementskosten</span>
                                <div className="mt-4 pt-4 border-t border-white/10 w-full text-xs text-gray-300 space-y-2">
                                    <div>✅ Eigen Logo Upload</div>
                                    <div>✅ Onbeperkt Aantal Spelers</div>
                                    <div>✅ Automatische Afhandeling</div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-10">
                            <Button 
                                onClick={() => navigate('/login?redirectTo=/wkpoule/dashboard')}
                                size="xl" 
                                className="bg-white text-black hover:bg-gray-200 font-black rounded-xl px-10 py-4 border-0"
                            >
                                Maak Nu Je Poule Aan
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* 🧠 SEO TEXT CLUSTER (Hier smult Google van!) */}
            <section className="py-20 bg-[#0a0a0a] text-gray-400 border-t border-white/5">
                <div className="max-w-4xl mx-auto px-6 text-sm leading-relaxed space-y-12">
                    
                    <div>
                        <h2 className="text-white font-bold text-xl mb-4">Waarom een WK 2026 Bedrijfspoule organiseren?</h2>
                        <p className="mb-4">
                            Het organiseren van een <strong>WK bedrijfspoule</strong> is de makkelijkste én leukste manier om de onderlinge band op de werkvloer te versterken. Voetbal leeft, of men er nu verstand van heeft of niet. Met de WK Poule software van ProPoule creëert u binnen één minuut een professionele omgeving waar collega's de uitslagen kunnen voorspellen, jokers kunnen inzetten en elkaars positie op de ranglijst live kunnen volgen.
                        </p>
                        <p>
                            Vergeet het geklooi met ingewikkelde Excel-bestanden die per e-mail rondgestuurd moeten worden. Onze software rekent automatisch alle standen uit na het eindsignaal van de scheidsrechter. Dit bespaart u uren aan administratief werk en voorkomt fouten in de puntentelling.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2"><CheckCircle size={16} className="text-orange-500" /> Voetbalpoule op Kantoor</h3>
                            <p>
                                Een voetbalpool op kantoor is dé ideale teambuilding activiteit tijdens een groot toernooi. Het zorgt voor gespreksstof bij het koffiezetapparaat en brengt collega's van verschillende afdelingen samen. Omdat onze website volledig responsive is, kunnen deelnemers overal — thuis, onderweg of op de zaak — via hun mobiele telefoon hun voorspellingen invoeren.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2"><CheckCircle size={16} className="text-orange-500" /> Bedrijfspoule met eigen Logo</h3>
                            <p>
                                Wil u het écht professioneel aanpakken? Onze premium versie stelt u in staat om uw <strong>eigen bedrijfslogo</strong> te uploaden. De gehele online omgeving krijgt hiermee direct de merkbeleving van uw organisatie. Dit maakt het platform perfect om in te zetten als relatiegeschenk voor personeel of zakelijke connecties.
                            </p>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-white font-bold text-lg mb-4">Veelgestelde vragen over de Online Poule</h3>
                        <div className="space-y-4">
                            <div>
                                <strong className="text-white block mb-1">Tot wanneer kunnen voorspellingen worden ingevuld?</strong>
                                Deelnemers kunnen hun voorspelling aanpassen tot exact één minuut voor de aftrap van een wedstrijd. Zo blijft het spel spannend tot het allerlaatste moment.
                            </div>
                            <div>
                                <strong className="text-white block mb-1">Welke uitslag telt voor de puntentelling?</strong>
                                Zoals gebruikelijk telt de stand na de officiële reguliere speeltijd (90 minuten plus blessuretijd). Eventuele verlengingen en strafschoppenseries tellen niet mee voor de pool, tenzij anders vermeld.
                            </div>
                            <div>
                                <strong className="text-white block mb-1">Is er een limiet aan het aantal deelnemers?</strong>
                                Nee. Of u nu een poule organiseert voor een klein MKB-bedrijf van 10 man of een multinational met honderden werknemers, onze servers schalen automatisch mee om iedereen een soepele ervaring te bieden.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 🦶 FOOTER */}
            <footer className="py-10 text-center border-t border-white/5 bg-black text-gray-600 text-xs font-medium">
                <div className="flex justify-center gap-6 mb-4">
                    <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacybeleid</button>
                    <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Algemene Voorwaarden</button>
                </div>
                <p>&copy; {new Date().getFullYear()} ProPoule. Alle rechten voorbehouden. Ontwikkeld voor het WK 2026.</p>
            </footer>
        </div>
    );
};
